import type { Lens, LensAudience } from '~/types/leLens';

/**
 * The library's destinations, in the order the navigation lists them. A
 * single team space is addressed as `space:<id>`, which is why this is a string
 * union widened by a template rather than a plain enum.
 */
export const LENS_LIBRARY_DESTINATIONS = ['home', 'mine', 'teams', 'certified'] as const;

const RENAMED_DESTINATIONS: Record<string, (typeof LENS_LIBRARY_DESTINATIONS)[number]> = {
  'for-you': 'home',
  favourites: 'home',
  spaces: 'teams',
  company: 'teams',
  library: 'teams',
  // Shared with me and Every Lens became chips on My Lenses; legacyMineFilter says which one.
  shared: 'mine',
  all: 'mine',
};

/** The My Lenses chip an old Shared with me or Every Lens link stands for. */
export function legacyMineFilter(params: URLSearchParams): 'shared-with-you' | 'every' | null {
  const value = params.get(LENS_LIBRARY_DESTINATION_PARAM)?.trim();
  if (value === 'shared') return 'shared-with-you';
  if (value === 'all') return 'every';
  return null;
}
export type LensLibraryDestination = (typeof LENS_LIBRARY_DESTINATIONS)[number] | `space:${string}`;

/**
 * The Lens library is a view inside the unified askRichard route, so its selected
 * destination belongs in that route's query string rather than in a second
 * standalone URL. Keeping the default out of the address makes the common link
 * short while still making team and sharing views bookmarkable.
 */
export const LENS_LIBRARY_DESTINATION_PARAM = 'library';

export function decodeLensLibraryDestination(params: URLSearchParams): LensLibraryDestination {
  const value = params.get(LENS_LIBRARY_DESTINATION_PARAM)?.trim();
  if (!value) {
    return 'home';
  }
  if (RENAMED_DESTINATIONS[value]) {
    return RENAMED_DESTINATIONS[value];
  }
  if ((LENS_LIBRARY_DESTINATIONS as ReadonlyArray<string>).includes(value)) {
    return value as LensLibraryDestination;
  }
  if (value.startsWith('space:') && value.slice('space:'.length).trim()) {
    return value as `space:${string}`;
  }
  return 'home';
}

export function applyLensLibraryDestination(
  current: URLSearchParams,
  destination: LensLibraryDestination,
): URLSearchParams {
  const next = new URLSearchParams(current);
  if (destination === 'home') {
    next.delete(LENS_LIBRARY_DESTINATION_PARAM);
  } else {
    next.set(LENS_LIBRARY_DESTINATION_PARAM, destination);
  }
  return next;
}

export const LENS_LIBRARY_LABELS: Record<(typeof LENS_LIBRARY_DESTINATIONS)[number], string> = {
  home: 'Lenses',
  mine: 'My Lenses',
  teams: 'Library',
  certified: 'Library',
};

/** Up to this many Lenses in Home's Recent row. */
export const CONTINUE_WORKING_LIMIT = 4;

/** Keeps repeated governed sources from reading like duplicated metadata. */
export function uniqueLensLabels(labels: Array<string>): Array<string> {
  const seen = new Set<string>();
  return labels.filter((label) => {
    const normalized = label.trim().toLocaleLowerCase();
    if (seen.has(normalized)) {
      return false;
    }
    seen.add(normalized);
    return true;
  });
}

export function spaceIdFromDestination(destination: LensLibraryDestination): string | null {
  return destination.startsWith('space:') ? destination.slice('space:'.length) : null;
}

const sameEmail = (left: string | null | undefined, right: string | null | undefined): boolean => {
  if (!left || !right) {
    return false;
  }
  return left.trim().toLowerCase() === right.trim().toLowerCase();
};

/**
 * The audience as a plain sentence fragment rather than a status chip.
 *
 * "Draft" and "Published" described a publication lifecycle; they never answered
 * the question a reader actually has in front of a list of other people's work,
 * which is who else can open this.
 */
export function lensAudienceLabel(audience: LensAudience | undefined, recipientCount = 0): string {
  if (audience === 'company') {
    return 'Everyone';
  }
  if (audience === 'reporting-admins') {
    return 'Admin';
  }
  if (audience === 'team') {
    return 'Team members';
  }
  if (audience === 'people') {
    return recipientCount === 1 ? '1 person' : `${recipientCount} people`;
  }
  return 'Only me';
}

export function lensHomeLabel(lens: Pick<Lens, 'home' | 'ownerEmail'>, viewerEmail?: string | null): string {
  if (lens.home?.kind === 'space') {
    return lens.home.spaceName;
  }
  return sameEmail(lens.ownerEmail, viewerEmail) ? 'My Lenses' : `Shared by ${ownerFirstName(lens.ownerEmail)}`;
}

/**
 * The only display name available at list grain: `ownerEmail` carries no display
 * name, so this reads the first segment of the local part and capitalises it.
 */
export function ownerFirstName(ownerEmail: string): string {
  const local = ownerEmail.split('@')[0] ?? '';
  const first = local.split(/[._-]/)[0] ?? '';
  return first ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase() : ownerEmail;
}

/** The list supplies an email only; retain every name segment instead of dropping the surname. */
export function ownerDisplayName(ownerEmail: string): string {
  const local = ownerEmail.trim().split('@')[0] ?? '';
  return (
    local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ') || ownerEmail
  );
}

export function isOwnLens(lens: Pick<Lens, 'ownerEmail'>, viewerEmail?: string | null): boolean {
  return sameEmail(lens.ownerEmail, viewerEmail);
}

/**
 * Direct shares include named shares and personal work accessible to reporting admins. Team work is excluded on
 * purpose: a space with fifty Lenses would otherwise bury the three a colleague
 * deliberately sent.
 */
export function isDirectShare(
  lens: Pick<Lens, 'ownerEmail' | 'sharedBy' | 'audience' | 'access'>,
  viewerEmail?: string | null,
): boolean {
  return (
    (Boolean(lens.sharedBy) ||
      lens.audience === 'reporting-admins' ||
      (lens.audience === 'people' && Boolean(lens.access?.canEdit))) &&
    !isOwnLens(lens, viewerEmail)
  );
}

/** A Lens another person shared with this reader by name: not a team's, not company-wide, not admin access. */
export function isSharedWithYou(
  lens: Pick<Lens, 'ownerEmail' | 'sharedBy' | 'audience' | 'access'>,
  viewerEmail?: string | null,
): boolean {
  return lens.audience === 'people' && isDirectShare(lens, viewerEmail);
}

export function isTeamLens(lens: Pick<Lens, 'home'>): boolean {
  return lens.home?.kind === 'space';
}

export function isCertifiedLens(lens: Pick<Lens, 'certification'>): boolean {
  return lens.certification?.badge.kind === 'certified';
}

/** Whether this reader may change who can see the Lens. Absent access means no. */
export function canShareLens(lens: Pick<Lens, 'access'>): boolean {
  return Boolean(lens.access?.canShare);
}

export function canEditLens(lens: Pick<Lens, 'access'>): boolean {
  return Boolean(lens.access?.canEdit);
}

export function canDeleteLens(lens: Pick<Lens, 'access'>): boolean {
  return Boolean(lens.access?.canDelete);
}

export function lensesForDestination(
  lenses: Array<Lens>,
  destination: LensLibraryDestination,
  viewerEmail?: string | null,
): Array<Lens> {
  const spaceId = spaceIdFromDestination(destination);
  if (spaceId) {
    return lenses.filter((lens) => lens.home?.kind === 'space' && lens.home.spaceId === spaceId);
  }
  if (destination === 'mine') {
    return lenses.filter(
      (lens) => (isOwnLens(lens, viewerEmail) && !isTeamLens(lens)) || isSharedWithYou(lens, viewerEmail),
    );
  }
  if (destination === 'teams') {
    return lenses.filter((lens) => isTeamLens(lens));
  }
  if (destination === 'certified') {
    return lenses.filter((lens) => isCertifiedLens(lens));
  }
  return lenses;
}

const changedAt = (lens: Pick<Lens, 'updatedAt'>): number => {
  const at = new Date(lens.updatedAt).getTime();
  return Number.isFinite(at) ? at : 0;
};

/**
 * Recently changed work the reader has a personal stake in: their own Lenses,
 * the ones colleagues named them on, and their teams'. The company catalogue is
 * excluded, because eighteen published dashboards would otherwise fill a row
 * meant for the four things they were in the middle of.
 */
export function continueWorking(
  lenses: Array<Lens>,
  viewerEmail: string | null | undefined,
  limit: number = CONTINUE_WORKING_LIMIT,
): Array<Lens> {
  return lenses
    .filter((lens) => isOwnLens(lens, viewerEmail) || isDirectShare(lens, viewerEmail) || isTeamLens(lens))
    .slice()
    .sort((left, right) => changedAt(right) - changedAt(left))
    .slice(0, Math.max(0, limit));
}

/**
 * Match saved content as well as Lens identity, so readers can find a Lens by
 * the chart or metric they remember rather than needing its exact title.
 */
export function searchLibrary(lenses: Array<Lens>, query: string, viewerEmail?: string | null): Array<Lens> {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) {
    return lenses;
  }
  return lenses.filter((lens) =>
    [
      lens.title,
      lens.nativePage ? lens.description : null,
      lensHomeLabel(lens, viewerEmail),
      lens.ownerEmail,
      ...lens.spec.cards.flatMap((card) => [card.title, card.insightQuestion ?? '']),
      ...(lens.spec.datasets ?? []).map((dataset) => dataset.name),
    ]
      .join(' ')
      .toLocaleLowerCase()
      .includes(needle),
  );
}

/** A deployed page is already released; draft status only limits its audience to its team. */
export function isDraftLens(lens: Pick<Lens, 'status' | 'nativePage'>): boolean {
  return lens.status === 'draft' && !lens.nativePage;
}
